import { FC, useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import isMobilePhone from 'validator/lib/isMobilePhone';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { default as OTPService } from '../services/OTP';
import OTPValidate from '../components/OTPValidate';
import OTPSetup from '../components/OTPSetup';
import Constants from '../../constants/constants';
import LoaderAnimation from '../components/LoaderAnimation';

const PageContainer = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  padding-top: 15vh;
`;

enum PageState {
  Loading = 0,
  Validate = 1,
  Setup = 2,
  Error = 3,
}

export const OTP: FC = () => {
  const [error, setError] = useState<string | false>(false);
  const [pageState, setPageState] = useState<PageState>(PageState.Loading);
  const [phone, setPhone] = useState<string>();
  // TODO: Set up typing for redux store
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { currentUserId, otpConfigured, otpPhoneTwoDigits } = useSelector((state: any) => state.user);
  const setupInputRef = useRef<HTMLInputElement>(null);
  const validateInputRef = useRef<HTMLInputElement>(null);
  const { pathname } = useLocation();
  const currURL = new URL(window.location.href);
  const purl = encodeURI(currURL.origin);
  const isChangePage = pathname.includes('/otp/change');

  useEffect(() => {
    if (isChangePage && !otpPhoneTwoDigits) {
      window.location.href = `${purl}/`;
    } else if (!otpConfigured) {
      setPageState(PageState.Setup);
    } else {
      setPageState(PageState.Validate);
    }
  }, []);

  useEffect(() => {
    if (pageState === PageState.Setup) {
      setupInputRef.current?.focus();
    }

    if (pageState === PageState.Validate) {
      validateInputRef.current?.focus();
    }
  }, [pageState]);

  const verifyOtp = (otpCode?: string) => {
    setError(false);
    if (otpCode?.length !== Constants.OTP_CODE_LENGTH) {
      setError('Please enter a valid OTP code');
      return false;
    }

    OTPService.validateOtp(otpCode, phone).then((response) => {
      const redirectUrl = isChangePage ? `${purl}/profile/${currentUserId}` : `${purl}/`;
      if (response.valid) {
        window.location.href = redirectUrl;
      } else {
        setError(response.error || 'Invalid access code. Please try again.');
      }
    });
  };

  const cancelOtp = () => {
    if (isChangePage) {
      window.location.href = `${purl}/profile/${currentUserId}`;
    } else {
      window.location.href = `${purl}/logout`;
    }
  };

  const resendOtp = () => {
    setError(false);
    OTPService.requestOtp().then((success) => {
      success && setPageState(PageState.Validate);
    });
  };

  const sanitizePhoneNumber = (phone?: string): string | false => {
    if (phone && isMobilePhone(phone, 'en-US')) {
      const sanitized = phone.replace(/[^0-9]/g, '');
      let formatted = '+';

      if (sanitized.length === 10) {
        formatted += `1${sanitized}`;
      } else {
        formatted += sanitized;
      }

      return formatted;
    }

    return false;
  };

  const setupOtp = (phone?: string) => {
    setError(false);
    const validPhone = sanitizePhoneNumber(phone);

    if (validPhone) {
      setPhone(validPhone);

      OTPService.requestOtp(validPhone).then((success) => {
        success && setPageState(PageState.Validate);
      });
    }
  };

  const lastPhoneDigits = phone ? phone.slice(-2) : otpPhoneTwoDigits;

  return (
    <PageContainer>
      {pageState === PageState.Loading && <LoaderAnimation />}
      {pageState === PageState.Validate && (
        <OTPValidate
          submitCallback={verifyOtp}
          cancelCallback={cancelOtp}
          resendCallback={resendOtp}
          lastPhoneDigits={lastPhoneDigits}
          error={error}
          ref={validateInputRef}
        />
      )}
      {pageState === PageState.Setup && (
        <OTPSetup
          submitCallback={setupOtp}
          cancelCallback={cancelOtp}
          error={error}
          ref={setupInputRef}
          isChangePage={isChangePage}
        />
      )}
    </PageContainer>
  );
};

export default OTP;
